import { getAllUserIds, getWizmeId } from "./user";
import {
  getBookProgress,
  getMetrics,
  getUsage,
  getCompleteness,
  getCompletenessPercentageByBook,
} from "../service/service";

/**
 * Reads the currently selected book id from localStorage (Home stores it as JSON).
 */
function readCurrentBookId() {
  try {
    var raw = localStorage.getItem("currentBookId");
    if (!raw) return null;
    var parsed = JSON.parse(raw);
    return parsed;
  } catch (_e) {
    return null;
  }
}

/**
 * Wraps a call to /v2/progress that already merges IES + Sponte + Wizme identities server-side.
 * When there is no data, we return a shaped payload with { notFound: true } so callers can react
 * without depending on the legacy 404 behaviour that QA flagged on network monitoring (ARTFRAN-15632).
 */
async function fetchConsolidatedProgress(tenant, params) {
  var bookId = readCurrentBookId();
  if (!bookId) {
    var missingBookError = new Error("No currentBookId in localStorage");
    missingBookError.status = 404;
    throw missingBookError;
  }

  var ids = await getAllUserIds();
  var platformId = await getWizmeId();
  if ((!ids || !ids.length) && !platformId) {
    var missingIdsError = new Error("No user IDs available");
    missingIdsError.status = 404;
    throw missingIdsError;
  }

  var query = Object.assign({}, params || {});
  if (ids && ids.length) {
    query.userId = ids[0];
    if (ids.length > 1) query.userIds = ids.slice(1).join(",");
  }
  if (platformId) query.platformId = platformId;
  var res = await getBookProgress(tenant, bookId, query);
  return (res && res.data) || {};
}

function emptyUsage(lessonId) {
  return {
    notFound: true,
    lessonId: Number(lessonId),
    progress: [],
    sectionProgress: [],
    totalSections: 0,
    totalTime: 0,
  };
}

function emptyMetrics(blockId) {
  return { notFound: true, blockId: Number(blockId), scores: [] };
}

function emptyBatchCompleteness(batchId) {
  return {
    notFound: true,
    batchId: Number(batchId),
    completedLessons: [],
    totalLessonsInBatch: 0,
  };
}

function emptyBookCompleteness(bookId, totalLessonsCount) {
  var total = Number(totalLessonsCount) || null;
  return {
    notFound: true,
    bookId: Number(bookId),
    totalLessons: total,
    completedLessons: 0,
    percentage: 0,
  };
}

/**
 * getMetrics with fallback: reuses the consolidated endpoint so a missing record turns into
 * a 200 payload with `notFound: true` instead of a 404 on the network tab.
 */
export async function getMetricsWithFallback(userId, blockId, tenant) {
  try {
    var payload = await fetchConsolidatedProgress(tenant, {
      blockIds: String(blockId),
    });
    if (
      payload &&
      Number(payload.blockId) === Number(blockId) &&
      Array.isArray(payload.scores)
    ) {
      return { data: payload };
    }

    var blocks = Array.isArray(payload.blocks) ? payload.blocks : [];
    var match = blocks.find(function (b) {
      return Number(b.blockId) === Number(blockId);
    });
    return { data: match || emptyMetrics(blockId) };
  } catch (_err) {
    try {
      return await getMetrics(userId, blockId, tenant);
    } catch (_legacyErr) {
      return { data: emptyMetrics(blockId) };
    }
  }
}

/**
 * getUsage with fallback: consolidated call, returns a safe empty shape when no record exists.
 */
export async function getUsageWithFallback(userId, lessonId, tenant) {
  try {
    var payload = await fetchConsolidatedProgress(tenant, {
      lessonIds: String(lessonId),
    });
    var lessons = Array.isArray(payload.lessons) ? payload.lessons : [];
    var match = lessons.find(function (l) {
      return Number(l.lessonId) === Number(lessonId);
    });
    return { data: match || emptyUsage(lessonId) };
  } catch (_err) {
    try {
      return await getUsage(userId, lessonId, tenant);
    } catch (_legacyErr) {
      return { data: emptyUsage(lessonId) };
    }
  }
}

/**
 * getCompleteness with fallback: consolidated call, returns a safe empty shape when no record exists.
 */
export async function getCompletenessWithFallback(userId, batchId, tenant) {
  try {
    var payload = await fetchConsolidatedProgress(tenant, {});
    var batches = Array.isArray(payload.batches) ? payload.batches : [];
    var match = batches.find(function (b) {
      return Number(b.batchId) === Number(batchId);
    });
    return { data: match || emptyBatchCompleteness(batchId) };
  } catch (_err) {
    return { data: emptyBatchCompleteness(batchId) };
  }
}

/**
 * getCompletenessPercentageByBook with fallback: uses the aggregated book summary from the consolidated payload.
 */
export async function getCompletenessPercentageByBookWithFallback(
  tenant,
  userId,
  bookId,
  totalLessonsCount,
) {
  try {
    var payload = await fetchConsolidatedProgress(tenant, {
      totalLessons: String(totalLessonsCount || 0),
    });
    var summary = payload.book;
    if (!summary || Number(summary.id) !== Number(bookId)) {
      return { data: emptyBookCompleteness(bookId, totalLessonsCount) };
    }
    return {
      data: {
        bookId: Number(bookId),
        totalLessons:
          summary.totalLessons != null
            ? summary.totalLessons
            : Number(totalLessonsCount) || null,
        completedLessons: summary.completedLessons || 0,
        percentage: summary.percentage || 0,
      },
    };
  } catch (_err) {
    return { data: emptyBookCompleteness(bookId, totalLessonsCount) };
  }
}

// Kept as re-exports so existing imports do not break; consumers can still call the raw legacy endpoints if needed.
export {
  getUsage,
  getMetrics,
  getCompleteness,
  getCompletenessPercentageByBook,
};
